// Config for the category drill-down view on the Reports > Team Members
// table (see team-reports memory / wave5-reports-fix-plan for background).
// Every category's table shares 3 fixed leading columns (Conversation,
// Timestamp, Performed By) -- only `extraColumns` differs per category.
// "Overview" (the existing per-member aggregate table) is NOT listed here --
// it's the sentinel default in ReportsTable.jsx, not a drill-down category.
//
// This is the single place that describes what a category *looks like*.
// Adding a new category later = one new entry here, nothing else changes --
// CategoryReportDetail.jsx renders columns generically off {label,key}
// pairs (row[col.key]), it never hardcodes a category's field names.
// Each category's `filters` array describes what "+ Add filter" offers for
// it. Two filter types exist:
// - `type: "member"` -- Assigned/Closed/Reassigned, scoped to team members
//   only, not AI/Workflow (Rahim's explicit call, 2026-08-12). Reassigned
//   has no bot.space reference to go off of, extended by analogy -- same
//   shape as Assigned (a contact handed to a specific member; the backend
//   already tracks it the same way, ReassignStamps/reassignStampsCount).
// - `type: "duration"` -- Response/Resolution/First Response Time, modeled
//   on bot.space's own "Median first response time" reference: a
//   time-range filter ("Response time"/"Resolution time"/"First response
//   time", `units` array -- Seconds/Minutes/Hours, matching bot.space's own
//   Unit dropdown) + a member filter ("Replied by"/"Resolved by"). Response
//   Time and Resolution Time also carry `includesCount: true` -- a plain
//   toggle (2026-08-13 redesign, see reports-category-drilldown memory)
//   that collapses the event list to one row per CONVERSATION and appends
//   a Count column, instead of a blind min/max threshold. First Response
//   Time deliberately has no includesCount -- a first response can only
//   happen once per conversation, so a per-conversation count would always
//   trivially be 1 (that aggregate lives on Overview's First Response
//   Count column instead).
//   All categories use CategoryDurationFilterChip / CategoryMemberFilterChip
//   generically via CategoryReportDetail.jsx's type switch -- this file
//   never hardcodes which chip to render.
export const CATEGORY_FIXED_COLUMNS = [
  { label: "Conversation", key: "conversationId" },
  { label: "Timestamp", key: "timestamp" },
  { label: "Performed By", key: "performedBy" },
];

export const CATEGORY_CONFIG = {
  assigned: {
    label: "Assigned",
    extraColumns: [{ label: "Assigned To", key: "assignedTo" }],
    filters: [{ key: "assignedTo", label: "Assigned to", type: "member" }],
  },
  closed: {
    label: "Closed",
    extraColumns: [{ label: "Closed by", key: "closedBy" }],
    filters: [{ key: "closedBy", label: "Closed by", type: "member" }],
  },
  reassigned: {
    label: "Reassigned",
    extraColumns: [{ label: "Reassigned To", key: "reassignedTo" }],
    filters: [{ key: "reassignedTo", label: "Reassigned to", type: "member" }],
  },
  responseTime: {
    label: "Response Time",
    extraColumns: [
      { label: "Replied By", key: "repliedBy" },
      { label: "Time", key: "time" },
    ],
    // Count folded INTO the duration filter (`includesCount: true`) instead
    // of being its own separate chip -- Rahim's explicit correction,
    // 2026-08-12. Redesigned again 2026-08-13 (see reports-category-drilldown
    // memory): it's now a plain toggle, no threshold value -- turning it on
    // collapses the event list to one row per CONVERSATION (Duration filter
    // narrows events first, then groups/counts what survives) and appends a
    // sortable Count column. Same mechanism now shared with Resolution Time
    // below -- no longer Response-Time-only.
    filters: [
      { key: "duration", label: "Response time", type: "duration", units: ["Seconds", "Minutes", "Hours"], includesCount: true },
      { key: "repliedBy", label: "Replied by", type: "member" },
    ],
  },
  resolutionTime: {
    label: "Resolution Time",
    extraColumns: [
      { label: "Resolved By", key: "resolvedBy" },
      { label: "Time", key: "time" },
    ],
    // Same includesCount toggle as Response Time above (2026-08-13 redesign)
    // -- the old standalone "Count" chip (blind min/max threshold, no
    // visible number anywhere) is gone.
    filters: [
      { key: "duration", label: "Resolution time", type: "duration", units: ["Seconds", "Minutes", "Hours"], includesCount: true },
      { key: "resolvedBy", label: "Resolved by", type: "member" },
    ],
  },
  firstResponseTime: {
    label: "First Response Time",
    // Also an Overview-table column (ReportsTable.jsx) -- this is the
    // SEPARATE drill-down-category version, added back 2026-08-12 (Rahim
    // wants both places, not one or the other).
    extraColumns: [
      { label: "Replied By", key: "repliedBy" },
      { label: "Time", key: "time" },
    ],
    // Deliberately NO includesCount here (2026-08-13) -- a "first response"
    // can only happen once per conversation ever, so grouping by
    // conversation would always trivially show Count: 1. That aggregate
    // already lives correctly as Overview's First Response Count column
    // (member-level, across all their conversations) -- see
    // reports-category-drilldown memory.
    filters: [
      { key: "duration", label: "First response time", type: "duration", units: ["Seconds", "Minutes", "Hours"] },
      { key: "repliedBy", label: "Replied by", type: "member" },
    ],
  },
};
// "Response Count" is NOT a drill-down category -- removed 2026-08-12
// (Rahim's explicit call) now that Count is folded into the duration
// filter instead of being a standalone thing. It still exists as an
// Overview-table column (ReportsTable.jsx) -- that's untouched.

export const getCategoryColumns = (categoryKey) => [
  ...CATEGORY_FIXED_COLUMNS,
  ...(CATEGORY_CONFIG[categoryKey]?.extraColumns || []),
];

// "Filter by Count" grouped mode (2026-08-15) -- a grouped row is a
// SUMMARY across potentially many events for one conversation, so
// Timestamp/Performed By (single-event fields) don't apply the way they do
// to a flat row -- reuses the same "Conversation" column definition
// (identity + click-through-to-Inbox behavior stays identical either mode)
// but swaps the rest for the grouped shape the backend actually returns
// (conversationId/count/lastTimestamp -- see categoryReports.service.js).
export const GROUPED_COLUMNS = [
  CATEGORY_FIXED_COLUMNS[0], // Conversation
  { label: "Team Member", key: "performerName" },
  { label: "Last Activity", key: "lastTimestamp" },
  { label: "Count", key: "count" },
];
